import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import HomePage from "@/pages/HomePage";
import ProductPage from "@/pages/ProductPage";
import PricingPage from "@/pages/PricingPage";
import ContactPage from "@/pages/ContactPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import BillingCanceledPage from "@/pages/BillingCanceledPage";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import AuditsList from "@/pages/AuditsList";
import NewAudit from "@/pages/NewAudit";
import AuditDetail from "@/pages/AuditDetail";
import ReportPage from "@/pages/Report";
import Layout from "@/components/Layout";
import Templates from "@/pages/Templates";
import TemplateEditor from "@/pages/TemplateEditor";
import TemplateCategory from "@/pages/TemplateCategory";
import AdminDashboard from "@/pages/AdminDashboard";
import ConsultantAccess from "@/pages/ConsultantAccess";
import CompanyAccessManagement from "@/pages/CompanyAccessManagement";
import SettingsAI from "@/pages/SettingsAI";
import SettingsBranding from "@/pages/SettingsBranding";
import CustomReportPage from "@/pages/CustomReport";
import BillingHub from "@/pages/BillingHub";
import Team from "@/pages/Team";
import CompanyChecklists from "@/pages/CompanyChecklists";
import ConsultantChecklists from "@/pages/ConsultantChecklists";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento…</div>;
  if (!user) return <Navigate to="/auth/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return <Layout>{children}</Layout>;
}

// Role sets — module-level constants keep referential equality across renders.
const ROLES_ADMIN = ["admin"];
const ROLES_TENANT = ["consultant", "company_user"];
const ROLES_CONSULTANT = ["consultant"];
const ROLES_COMPANY = ["company_user"];

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={user.role === "admin" ? "/app/admin" : "/app"} replace />;
  return children;
}

function AppHomeRoute() {
  const { user } = useAuth();
  if (user?.role === "admin") return <Navigate to="/app/admin" replace />;
  return <Dashboard />;
}

function LegacyAppRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={to || `/app${location.pathname}${location.search}`} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="/billing/cancel" element={<BillingCanceledPage />} />
            <Route path="/auth/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/auth/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/app" element={<Protected><AppHomeRoute /></Protected>} />
            <Route path="/app/admin" element={<Protected roles={ROLES_ADMIN}><AdminDashboard /></Protected>} />
            <Route path="/app/aziende" element={<Protected roles={ROLES_TENANT}><Companies /></Protected>} />
            <Route path="/app/collegamenti" element={<Protected roles={ROLES_CONSULTANT}><ConsultantAccess /></Protected>} />
            <Route path="/app/accessi-consulenti" element={<Protected roles={ROLES_COMPANY}><CompanyAccessManagement /></Protected>} />
            <Route path="/app/team" element={<Protected roles={["admin", "consultant"]}><Team /></Protected>} />
            <Route path="/app/checklist-azienda" element={<Protected roles={ROLES_COMPANY}><CompanyChecklists /></Protected>} />
            <Route path="/app/checklist-azienda/:assignmentId" element={<Protected roles={ROLES_COMPANY}><CompanyChecklists /></Protected>} />
            <Route path="/app/checklist-assegnate" element={<Protected roles={ROLES_CONSULTANT}><ConsultantChecklists /></Protected>} />
            <Route path="/app/audit" element={<Protected roles={ROLES_TENANT}><AuditsList /></Protected>} />
            <Route path="/app/audit/nuovo" element={<Protected roles={ROLES_TENANT}><NewAudit /></Protected>} />
            <Route path="/app/audit/:id" element={<Protected roles={ROLES_TENANT}><AuditDetail /></Protected>} />
            <Route path="/app/audit/:id/relazione" element={<Protected roles={ROLES_TENANT}><ReportPage /></Protected>} />
            <Route path="/app/audit/:id/verbale-custom" element={<Protected roles={ROLES_TENANT}><CustomReportPage /></Protected>} />
            <Route path="/app/template" element={<Protected roles={ROLES_CONSULTANT}><Templates /></Protected>} />
            <Route path="/app/template/nuovo" element={<Protected roles={ROLES_CONSULTANT}><TemplateEditor /></Protected>} />
            <Route path="/app/template/categoria/:category" element={<Protected roles={ROLES_CONSULTANT}><TemplateCategory /></Protected>} />
            <Route path="/app/template/categoria/:category/settore/:sector" element={<Protected roles={ROLES_CONSULTANT}><TemplateCategory /></Protected>} />
            <Route path="/app/template/:type" element={<Protected roles={ROLES_CONSULTANT}><TemplateEditor /></Protected>} />
            <Route path="/app/billing" element={<Protected roles={ROLES_CONSULTANT}><BillingHub /></Protected>} />
            <Route path="/app/impostazioni/ai" element={<Protected roles={ROLES_CONSULTANT}><SettingsAI /></Protected>} />
            <Route path="/app/impostazioni/branding" element={<Protected roles={ROLES_CONSULTANT}><SettingsBranding /></Protected>} />
            <Route path="/admin" element={<LegacyAppRedirect />} />
            <Route path="/aziende" element={<LegacyAppRedirect />} />
            <Route path="/collegamenti" element={<LegacyAppRedirect />} />
            <Route path="/accessi-consulenti" element={<LegacyAppRedirect />} />
            <Route path="/audit" element={<LegacyAppRedirect />} />
            <Route path="/audit/nuovo" element={<LegacyAppRedirect />} />
            <Route path="/audit/:id" element={<LegacyAppRedirect />} />
            <Route path="/audit/:id/relazione" element={<LegacyAppRedirect />} />
            <Route path="/audit/:id/verbale-custom" element={<LegacyAppRedirect />} />
            <Route path="/template" element={<LegacyAppRedirect />} />
            <Route path="/template/nuovo" element={<LegacyAppRedirect />} />
            <Route path="/template/:type" element={<LegacyAppRedirect />} />
            <Route path="/impostazioni/ai" element={<LegacyAppRedirect />} />
            <Route path="/impostazioni/branding" element={<LegacyAppRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
